import React, { useState, useEffect, useRef } from 'react';
import NavigationDropdown from './NavigationDropdown.jsx';

/**
 * PremiumNav
 * ---------
 * Wraps NavigationDropdown and appends a membership badge when the current
 * visitor has an active **paid** subscription.
 *
 * Mount pattern (default.hbs, behind the `enable_react_nav` feature flag):
 *
 *   <div id="react-nav-root"
 *        data-react-component="PremiumNav"
 *        data-is-member="{{#if @member}}true{{else}}false{{/if}}">
 *     {{> "navigation"}}   ← HBS fallback, scraped by index.jsx before mount
 *   </div>
 *
 * Props injected by index.jsx from data-* attributes:
 *   navigation {Array}  – already-scraped + normalised nav items
 *   isMember   {string} – "true" | "false"  (from Handlebars {{@member}})
 *
 * Membership tier is always confirmed client-side via /ghost/api/members/me/
 * so the badge never appears speculatively. The main nav renders immediately
 * from the pre-scraped props — no loading state is ever shown for nav links.
 */
const PremiumNav = ({ navigation = [], isMember: isMemberProp = 'false' }) => {
    const hbsIsMember = isMemberProp === 'true';

    const [member, setMember] = useState({
        resolved: false,
        isPaid: false,
        tier: null,
        email: null,
    });

    const abortRef = useRef(null);

    useEffect(() => {
        // Skip the network round-trip entirely when HBS already knows the visitor
        // is not a member — avoids a superfluous 401 on every page load for guests.
        if (!hbsIsMember) {
            setMember({ resolved: true, isPaid: false, tier: null, email: null });
            return;
        }

        abortRef.current = new AbortController();

        fetch('/ghost/api/members/me/', {
            credentials: 'include',
            signal: abortRef.current.signal,
            headers: { 'Content-Type': 'application/json' },
        })
            .then((r) => {
                if (!r.ok) throw new Error('not-authenticated');
                return r.json();
            })
            .then((data) => {
                const subs = Array.isArray(data?.subscriptions) ? data.subscriptions : [];
                const activeSub = subs.find((s) => s.status === 'active');
                const tier = activeSub?.tier?.name ?? null;
                const isPaid = activeSub?.tier?.type === 'paid';

                setMember({
                    resolved: true,
                    isPaid,
                    tier,
                    email: data?.email ?? null,
                });
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setMember({ resolved: true, isPaid: false, tier: null, email: null });
                }
            });

        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [hbsIsMember]);

    const { isPaid, tier, resolved } = member;

    // Premium-only nav items — appended after the standard nav set.
    // Extend this array to gate additional pages behind paid membership.
    const premiumNavItems = isPaid
        ? [{ label: 'Archive', url: '/archive/' }]
        : [];

    const navItems =
        Array.isArray(navigation) && navigation.length > 0
            ? [...navigation, ...premiumNavItems]
            : premiumNavItems;

    return (
        <div style={{ display: 'contents' }}>
            {/* Standard + premium-gated nav links */}
            <NavigationDropdown navigation={navItems} />

            {/* Premium badge — only rendered after API confirms a paid tier */}
            {resolved && isPaid && (
                <PremiumBadge tier={tier} />
            )}
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* PremiumBadge                                                                 */
/* -------------------------------------------------------------------------- */

const BADGE_STYLES = {
    wrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.28rem',
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#78350f',
        background: 'linear-gradient(135deg, #fef9c3 0%, #fde047 52%, #fbbf24 100%)',
        border: '1px solid rgba(202, 138, 4, 0.38)',
        borderRadius: '999px',
        padding: '0.2rem 0.58rem',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        boxShadow:
            '0 1px 5px rgba(202, 138, 4, 0.22), inset 0 1px 0 rgba(255,255,255,0.5)',
        marginLeft: '0.7rem',
        cursor: 'default',
        userSelect: 'none',
        // Fades in after the API call resolves — no jarring pop-in
        animation: 'premiumBadgeFadeIn 0.35s ease both',
        flexShrink: 0,
    },
    star: {
        flexShrink: 0,
    },
};

const PremiumBadge = ({ tier }) => (
    <>
        {/* Keyframe injected once; idempotent if already present */}
        <style>{`
            @keyframes premiumBadgeFadeIn {
                from { opacity: 0; transform: translateY(3px) scale(0.92); }
                to   { opacity: 1; transform: translateY(0)   scale(1);    }
            }
            .premium-nav-badge:hover {
                filter: brightness(1.04) saturate(1.1);
                box-shadow: 0 3px 10px rgba(202, 138, 4, 0.32),
                            inset 0 1px 0 rgba(255,255,255,0.6) !important;
            }
        `}</style>
        <span
            className="premium-nav-badge"
            role="status"
            aria-label={`${tier || 'Premium'} member`}
            title={`${tier || 'Premium'} membership active`}
            style={BADGE_STYLES.wrapper}
        >
            <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                style={BADGE_STYLES.star}
            >
                {/* Star / premium indicator */}
                <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
            </svg>
            {tier || 'Premium'}
        </span>
    </>
);

export default PremiumNav;
